import React, { useState, useEffect } from "react";
import {
  Table,
  Card,
  Button,
  Modal,
  Input,
  Space,
  message,
  Row,
  Col,
  Statistic,
  Popconfirm,
  Tooltip,
  Tag,
} from "antd";
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  ReloadOutlined,
  AppstoreOutlined,
  TagsOutlined,EyeOutlined
} from "@ant-design/icons";

import courseService from "../../services/course.service";
import SubCategoryManager from "./SubCategoryManager";

const CategoryManager = () => {
  const [categories, setCategories] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [categoryName, setCategoryName] = useState("");


  const [subCategoryModalVisible, setSubCategoryModalVisible] = useState(false); // ✅ Ajout
  const [selectedCategoryForSub, setSelectedCategoryForSub] = useState(null); // ✅ Ajout

   const handleViewSubCategories = (category) => {
    setSelectedCategoryForSub(category);
    setSubCategoryModalVisible(true);
  };

  useEffect(() => {
    loadCategories();
    loadStats();
  }, []);

  const loadCategories = async () => {
    setLoading(true);
    try {
      const data = await courseService.getAllCategoriesAdmin();
      setCategories(data);
    } catch (error) {
      message.error("Erreur lors du chargement des catégories");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      const data = await courseService.getCategoriesStats();
      setStats(data);
    } catch (error) {
      console.error("Erreur lors du chargement des stats:", error);
    }
  };

  const handleCreate = () => {
    setEditMode(false);
    setSelectedCategory(null);
    setCategoryName("");
    setModalVisible(true);
  };

  const handleEdit = (category) => {
    setEditMode(true);
    setSelectedCategory(category);
    setCategoryName(category.libelle);
    setModalVisible(true);
  };

  const handleSubmit = async () => {
    if (!categoryName.trim()) {
      message.error("Le nom de la catégorie est requis");
      return;
    }

    try {
      if (editMode) {
      await courseService.updateCategory(selectedCategory.id, {
        libelle: categoryName,
      });
      message.success("Catégorie mise à jour avec succès");
    } else {
      await courseService.createCategory({
        libelle: categoryName,
      });
      message.success("Catégorie créée avec succès");
    }
      setModalVisible(false);
      loadCategories();
      loadStats();
    } catch (error) {
      if (error.response?.data?.error) {
        message.error(error.response.data.error);
      } else {
        message.error(
          editMode
            ? "Erreur lors de la mise à jour"
            : "Erreur lors de la création"
        );
      }
    }
  };

const handleDelete = async (id) => {
  try {
    await courseService.deleteCategory(id);
    message.success("Catégorie supprimée avec succès");
    loadCategories();
    loadStats();
  } catch (error) {
    if (error.response?.data?.error) {
      message.error(error.response.data.error);
    } else {
      message.error("Erreur lors de la suppression");
    }
  }
};

  const columns = [
    {
      title: "ID",
      dataIndex: "id",
      key: "id",
      width: 80,
      sorter: (a, b) => a.id - b.id,
    },
    {
      title: "Nom de la catégorie",
      dataIndex: "libelle",
      key: "libelle",
      render: (text) => (
        <div style={{ fontWeight: 500, fontSize: "14px" }}>{text}</div>
      ),
    },
    {
      title: "Sous-catégories",
      key: "subcategories",
      width: 150,
      render: (_, record) => (
        <Tag color="blue" icon={<TagsOutlined />}>
          {record.subCategories?.length || 0} sous-catégories
        </Tag>
      ),
    },
    {
      title: "Actions",
      key: "actions",
      width: 220, // ✅ Augmentez la largeur
      fixed: "right",
      render: (_, record) => (
        <Space size="small">
          <Tooltip title="Voir sous-catégories">
            <Button
              type="default"
              size="small"
              icon={<EyeOutlined />}
              onClick={() => handleViewSubCategories(record)}
            >
              Sous-cat
            </Button>
          </Tooltip>
          <Tooltip title="Modifier">
            <Button
              type="primary"
              size="small"
              icon={<EditOutlined />}
              onClick={() => handleEdit(record)}
            />
          </Tooltip>
          <Popconfirm
            title="Êtes-vous sûr de vouloir supprimer cette catégorie ?"
            description={
              record.subCategories?.length > 0
                ? "Cette catégorie contient des sous-catégories"
                : "Cette action est irréversible"
            }
            onConfirm={() => handleDelete(record.id)}
            okText="Oui"
            cancelText="Non"
            disabled={record.subCategories?.length > 0}
          >
            <Tooltip
              title={
                record.subCategories?.length > 0
                  ? "Impossible de supprimer: contient des sous-catégories"
                  : "Supprimer"
              }
            >
              <Button
                danger
                size="small"
                icon={<DeleteOutlined />}
                disabled={record.subCategories?.length > 0}
              />
            </Tooltip>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: "24px" }}>
      {stats && (
        <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
          <Col xs={24} sm={12}>
            <Card
              bordered={false}
              style={{
                borderLeft: "4px solid #1890ff",
                boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
              }}
            >
              <Statistic
                title="Total Catégories"
                value={stats.totalCategories || 0}
                prefix={<AppstoreOutlined style={{ color: "#1890ff" }} />}
                valueStyle={{ color: "#1890ff" }}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12}>
            <Card
              bordered={false}
              style={{
                borderLeft: "4px solid #52c41a",
                boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
              }}
            >
              <Statistic
                title="Total Sous-catégories"
                value={stats.totalSubcategories || 0}
                prefix={<TagsOutlined style={{ color: "#52c41a" }} />}
                valueStyle={{ color: "#52c41a" }}
              />
            </Card>
          </Col>
        </Row>
      )}

      <Card style={{ marginBottom: 16 }} bodyStyle={{ padding: "12px 16px" }}>
        <Space>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={handleCreate}
          >
            Nouvelle Catégorie
          </Button>
          <Button icon={<ReloadOutlined />} onClick={loadCategories} loading={loading}>
            Actualiser
          </Button>
        </Space>
      </Card>

      <Card bordered={false} style={{ boxShadow: "0 2px 8px rgba(0,0,0,0.1)" }}>
        <Table
          columns={columns}
          dataSource={categories}
          rowKey="id"
          loading={loading}
          pagination={{
            pageSize: 10,
            showSizeChanger: true,
            showTotal: (total) => `Total: ${total} catégories`,
          }}
        />
      </Card>

      <Modal
        title={editMode ? "Modifier la catégorie" : "Nouvelle catégorie"}
        open={modalVisible}
        onOk={handleSubmit}
        onCancel={() => setModalVisible(false)}
        okText={editMode ? "Mettre à jour" : "Créer"}
        cancelText="Annuler"
      >
        <div style={{ marginBottom: 16 }}>
          <div style={{ marginBottom: 8, fontWeight: 500 }}>
            Nom de la catégorie *
          </div>
          <Input
            placeholder="Ex: Développement Web"
            value={categoryName}
            onChange={(e) => setCategoryName(e.target.value)}
            onPressEnter={handleSubmit}
          />
        </div>
      </Modal>

       {/* ✅ Ajoutez ce composant à la fin, avant la dernière balise </div> */}
      <SubCategoryManager
        visible={subCategoryModalVisible}
        categoryId={selectedCategoryForSub?.id}
        categoryName={selectedCategoryForSub?.libelle}
        onClose={() => {
          setSubCategoryModalVisible(false);
          loadCategories(); // Recharge pour mettre à jour les compteurs
          loadStats();
        }}
      />

    </div>



  );
};

export default CategoryManager;